<template>
    <div id="login-page" class="d-flex align-items-center h-100">
        <landing-page></landing-page>
        <section class="login-section right order-first order-md-last">
            <div class="login-form">
                <h2 class="form-title">{{ __("title", this.translations.forgotPasswordPage) }}</h2>
                <h3 class="form-description">{{ __("description", this.translations.forgotPasswordPage) }}</h3>

                <qw-input class="form-field mb-2"
                          name="username"
                          type="text"
                          icon="fas fa-envelope"
                          placeholder="Username"
                          autocomplete="off"
                          v-model="form.username"/>
                <button class="form-button" @click="submit()" v-if="status === 'ready'"
                        :disabled="hasMissingValidation">{{
                    __('button', this.translations.forgotPasswordPage) }}
                </button>
                <button class="form-button" disabled v-if="status === 'loading'"><i
                        class="fas fa-circle-notch fa-spin"></i>{{ __('loading', this.translations.global) }}
                </button>
                <router-link :to="'login'" style="color: white!important;font-size: 14px">Remembered your password? Login</router-link>
                <div class="form-error mb-3" v-if="hasErrors">
                    <ul>
                        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                    </ul>
                </div>
            </div>
            <div class="languages bottom-section">
                <button :class="[ 'language-btn', { 'active': isCurrentLanguage('en') } ]" @click="setLanguage('en')">
                    EN
                </button>
                <button :class="[ 'language-btn', { 'active': isCurrentLanguage('ar') } ]" @click="setLanguage('ar')">
                    عر
                </button>
            </div>
        </section>
    </div>
</template>

<script>
    import QwInput from "@/components/global/form/QWInput";
    import Auth from "@/services/AuthService";
    import Validator from "@/utils/Validator";
    import LandingPage from "../components/LandingPage";

    export default {
        name: "forgot-password-page",
        components: {
            LandingPage,
            QwInput
        },
        data() {
            return {
                status: "ready",
                errors: [],
                form: {
                    username: ""
                },
                validationRules: {
                    username: "required"
                }
            };
        },
        mounted() {
            this.bindEnterKey();
        },
        destroyed() {
            this.unbindEnterKey();
        },
        methods: {
            bindEnterKey() {
                /**
                 * Used JavaScript instead of the VueJS event @keydown.enter="login()"
                 * since using VueJS events should be handled on the element
                 * in focus or multiple times on different elements...
                 */
                document.onkeydown = (e) => {
                    let keyCode = e.keyCode === undefined ? e.key : e.keyCode;
                    switch (keyCode) {
                        case 13:
                            this.submit();
                            break;
                    }
                }
            },
            unbindEnterKey() {
                document.onkeydown = null;
            },
            submit() {
                this.errors = [];
                let validator = new Validator();
                validator.validate(this.form, this.validationRules);
                if (validator.hasErrors()) {
                    this.errors = validator.list();
                    return;
                }

                this.submitForgotPassword();
            },
            async submitForgotPassword() {
                this.status = "loading";
                try {
                    Auth.forgotPassword(this.form.username)
                        .then(() => {
                            this.$router.push('login');
                            this.$store.commit("setToastMessage", {
                                title: 'Kindly check your email to reset your password',
                                type: 'success', hideIcon: false
                            });
                        })
                        .catch(() => {
                            this.$store.commit("setToastMessage", {
                                title: 'User does not exist',
                                type: 'error', hideIcon: false
                            });
                        });
                } catch (e) {
                    console.log(e);
                } finally {
                    this.status = "ready";
                }
            },
            setLanguage(language) {
                this.$store.dispatch("setLanguage", language);
            },
            isCurrentLanguage(language) {
                return this.$store.getters.appLanguage === language;
            }
        },
        computed: {
            hasMissingValidation() {
                return !(this.form.username);
            },
            hasErrors() {
                return !!(this.errors && this.errors.length);
            }
        }
    }
</script>

<style lang="scss" scoped>
    #login-page {
        overflow: auto;

        .login-section {
            display: flex;
            align-items: center;
            flex-direction: row;
            height: 100%;

            .centered {
                display: flex !important;
                justify-content: center !important;
            }

            .bottom-section {
                justify-content: center;
                position: absolute;
                width: 100%;
                bottom: 10px;
            }

            &.left {
                position: relative;

                .main-title {
                    font-family: $raleway;
                    color: $darkest-gray;
                    font-weight: 900;
                    line-height: 1.1;
                    text-transform: uppercase;
                    font-size: 40px;

                    .line {
                        display: flex;
                        align-items: center;
                    }
                }
            }

            &.right {
                position: relative;
                background-color: $primary;

                .login-form {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                    width: 100%;

                    .form-title {
                        width: 100%;
                        margin: 0 0 10px 0;
                        font-size: 22px;
                        font-weight: 700;
                        font-family: $raleway;
                        color: $white;
                        line-height: 1;
                        text-align: left;
                    }

                    .form-description {
                        width: 100%;
                        font-family: $roboto;
                        font-size: 16px;
                        line-height: 1.4;
                        color: $light-gray;
                        text-align: left;

                        a {
                            color: $light-gray !important;
                            text-decoration: underline;
                            cursor: pointer;

                            &:hover {
                                text-decoration: none;
                            }
                        }
                    }

                    .form-error {
                        width: 100%;
                        color: $white;
                        font-family: $lato;
                        font-weight: 400;
                        font-size: 15px;
                        color: $orange;

                        ul {
                            margin: 0;
                        }

                        i {
                            margin: 0px 7px 0px 0px;
                        }
                    }

                    .form-field {
                        width: 100%;
                    }

                    .form-button {
                        display: block;
                        width: 100%;
                        padding: 7px 0;
                        font-weight: 700;
                        font-family: $raleway;
                        border: 2px solid $white !important;
                        border-radius: 25px;
                        color: $primary;
                        background-color: $white;

                        i {
                            margin: 0 10px 0 0;
                        }

                        &:hover {
                            color: $white;
                            background-color: transparent;
                        }

                        &:disabled {
                            border: 2px solid $light-gray !important;
                            background-color: $light-gray;
                            color: $dark-gray;

                            &:hover {
                                background-color: $light-gray;
                            }
                        }
                    }
                }

                .languages {
                    display: flex;
                    align-items: center;

                    .language-btn {
                        margin: 0 5px 0 0;
                        padding: 1px 8px;
                        font-size: 12px;
                        font-weight: 700;
                        font-family: $raleway;
                        text-transform: uppercase;
                        color: $white;
                        background: none;
                        @include removeBootstrapOutline;
                        border: 2px solid transparent !important;
                        border-radius: 3px;
                        cursor: pointer;
                        @include fastestTransition;

                        &:hover, &.active {
                            border: 2px solid $white !important
                        }

                        &:last-child {
                            margin: 0;
                        }
                    }
                }
            }
        }
    }

    .is_valid {
        //color: rgba(136, 152, 170, 0.8);
        color: white;
    }

    .is_valid:before {
        width: 100%;
    }

    ul {
        padding-left: 20px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    li {
        margin-bottom: 8px;
        color: white;
        font-size: 12px;
        position: relative;
    }

    li:before {
        content: "";
        width: 0%;
        height: 2px;
        background: white;
        position: absolute;
        left: 0; top: 50%;
        display: block;
        font-size: 12px;
        transition: all .6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    .checked { animation: draw 0.5s ease forwards; }

    @keyframes draw {
        to {
            stroke-dashoffset: 0;
        }
    }

    #password-validation {
        width: 100%;
        text-align: left;
    }

    .dark {
        #login-page {
            .login-section {
                &.left {
                    .main-title {
                        color: $white;
                    }
                }

                &.right {
                    background-color: $extra-darker-gray;

                    .login-form {
                        .form-error {
                            color: $light-red;
                        }

                        .form-button {
                            color: $extra-darker-gray;

                            &:hover {
                                color: $white;
                            }

                            &:disabled {
                                color: $gray;
                                border: 2px solid $extra-dark-gray !important;
                                background-color: $extra-dark-gray;
                            }
                        }
                    }
                }
            }
        }
    }

    @media screen and (max-width: $breakpoint-md) {
        #login-page {
            flex-direction: column;

            .login-section {
                height: auto;
                flex-direction: column;

                .centered {
                    display: block;
                }

                .bottom-section {
                    position: static;
                    transform: translate(0);
                    padding: 10px 0;
                }

                &.left {
                    width: 100%;

                    .main-title {
                        font-size: 22px;

                        .line {
                            display: inline;
                        }
                    }
                }

                &.right {
                    width: 100%;

                    .login-form {
                        padding: 30px 20px;
                    }
                }
            }
        }
    }

    @media screen and (min-width: $breakpoint-md) {
        #login-page {
            .login-section {
                .centered {
                    display: block;
                }

                &.left {
                    width: 50%;

                    .main-title {
                        font-size: 30px;
                    }
                }

                &.right {
                    width: 50%;

                    .login-form {
                        padding: 0 25px;
                    }
                }
            }
        }
    }

    @media screen and (min-width: $breakpoint-lg) {
        #login-page {
            .login-section {
                &.left {
                    width: 70%;
                }

                &.right {
                    width: 30%;

                    .login-form {
                        padding: 0 35px;
                    }
                }
            }
        }
    }
</style>
